import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  EventEmitter,
  Input,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../../core/pipes/translate.pipe';
import { ApiService } from '../../../../../core/services/api.service';
import { Subscription } from 'rxjs';
import { RoundToTenthPipe } from '../../../../../core/pipes/round-to-tenth.pipe';
import { roundToTenth, toFiniteNumber } from '../../../../../core/utils/number-format.util';

interface CalendarDay {
  date: number;
  status: 'available' | 'unavailable' | 'past';
  isCurrentMonth: boolean;
  fullDate: Date;
  isToday: boolean;
  isSelected?: boolean;
  isInRange?: boolean;
  price?: number;
  priceLabel?: string;
}

interface AvailabilityPeriod {
  _id: string;
  startDate: string;
  endDate: string | null;
  status: string;
}

interface AvailabilityResponse {
  message: string;
  dataRes: AvailabilityPeriod[];
}

@Component({
  selector: 'app-availability-calendar',
  imports: [CommonModule, TranslatePipe, RoundToTenthPipe],
  templateUrl: './availability-calendar.html',
  styleUrl: './availability-calendar.scss',
})
export class AvailabilityCalendarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() unitId?: string;
  @Input() availabilityPeriods?: AvailabilityPeriod[] | null;
  @Input() seasonalPricing?: any;
  @Input() basePrice: number | string = 0;
  @Input() currency: string = 'EGP';
  @Output() dateRangeSelected = new EventEmitter<{ startDate: Date; endDate: Date; totalPrice?: number; totalDays?: number }>();

  currentDate = new Date();
  calendarMonth = new Date().getMonth();
  calendarYear = new Date().getFullYear();
  calendarDays: CalendarDay[] = [];
  availableCount = 0;

  // Date range selection
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  isSelectingRange = false;
  selectedTotalDays = 0;
  selectedTotalPrice = 0;
  selectedTotalPriceLabel = '0';

  // API data
  private resolvedAvailabilityPeriods: AvailabilityPeriod[] = [];
  seasonalPricings: any[] = [];
  private subscription: Subscription | null = null;
  isLoading = false;
  errorMessage = '';

  constructor(private apiService: ApiService) {}

  ngOnInit() {
    if (!this.unitId && !Array.isArray(this.availabilityPeriods)) {
      this.generateCalendar();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seasonalPricing'] && this.seasonalPricing) {
      // Add the preloaded seasonal pricing if it's not already in the array
      const exists = this.seasonalPricings.some(sp => sp._id === this.seasonalPricing._id);
      if (!exists) {
        this.seasonalPricings.push(this.seasonalPricing);
        this.generateCalendar();
        this.calculateSelectionPrice();
      }
    }
    
    if ('unitId' in changes || 'availabilityPeriods' in changes) {
      this.initializeAvailabilitySource();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private initializeAvailabilitySource(): void {
    if (this.tryUsePreloadedAvailability()) {
      return;
    }

    if (this.unitId) {
      this.loadSeasonalPricing();
      this.loadAvailability();
      return;
    }

    this.generateCalendar();
  }

  private loadSeasonalPricing(): void {
    if (!this.unitId) return;
    this.apiService.get<any>(`/seasonalPricing/unit/${this.unitId}`).subscribe({
      next: (response) => {
        this.seasonalPricings = response?.seasonalPricings || [];
        this.generateCalendar();
        this.calculateSelectionPrice();
      },
      error: (error) => {
        console.error('Error loading seasonal pricing:', error);
      }
    });
  }

  private tryUsePreloadedAvailability(): boolean {
    if (!Array.isArray(this.availabilityPeriods)) {
      return false;
    }

    this.subscription?.unsubscribe();
    this.resolvedAvailabilityPeriods = this.availabilityPeriods;
    this.isLoading = false;
    this.errorMessage = '';
    this.generateCalendar();
    return true;
  }

  loadAvailability(): void {
    if (!this.unitId) return;

    this.subscription?.unsubscribe();
    this.isLoading = true;
    this.errorMessage = '';

    this.subscription = this.apiService
      .get<AvailabilityResponse>(`/availability/${this.unitId}/unit-availability`)
      .subscribe({
        next: (response) => {
          console.log('Availability Response:', response);
          this.resolvedAvailabilityPeriods = Array.isArray(response?.dataRes)
            ? response.dataRes
            : [];
          this.generateCalendar();
          this.isLoading = false;
        },
        error: (error) => {
          console.warn('unit-availability failed, trying owner endpoint. Error:', error);
          // Try owner endpoint as a fallback (some deployments use /:unitId/owner)
          this.apiService.get<any>(`/availability/${this.unitId}/owner`).subscribe({
            next: (res2) => {
              // owner endpoint may return { availabilities: [...] }
              const list = Array.isArray(res2?.availabilities)
                ? res2.availabilities
                : Array.isArray(res2?.dataRes)
                  ? res2.dataRes
                  : [];
              // Normalize to AvailabilityPeriod[] shape
              this.resolvedAvailabilityPeriods = list.map((p: any) => ({
                _id: p._id || p.id || '',
                startDate: p.startDate,
                endDate: p.endDate ?? null,
                status: p.status,
              }));
              this.generateCalendar();
              this.isLoading = false;
            },
            error: (err2) => {
              console.error('Error loading availability (fallback):', err2);
              this.errorMessage = 'Failed to load availability';
              this.isLoading = false;
              this.generateCalendar();
            },
          });
        },
      });
  }

  generateCalendar(): void {
    const firstDay = new Date(this.calendarYear, this.calendarMonth, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    this.availableCount = 0;

    for (let i = 0; i < 42; i++) {
      const current = new Date(startDate);
      current.setDate(startDate.getDate() + i);

      const isCurrentMonth = current.getMonth() === this.calendarMonth;
      const isPast = current < new Date(new Date().setHours(0, 0, 0, 0));

      let status: 'available' | 'unavailable' | 'past';

      if (isPast) {
        status = 'past';
      } else {
        status = this.getDateStatus(current);
        if (status === 'available' && isCurrentMonth) {
          this.availableCount++;
        }
      }

      const isSelected = this.isDateSelected(current);
      const isInRange = this.isDateInRange(current);
      const price = this.getPriceForDate(current);
      const priceLabel = this.formatCalendarNumber(price);

      days.push({
        date: current.getDate(),
        status,
        isCurrentMonth,
        fullDate: new Date(current),
        isToday: this.isToday(current),
        isSelected,
        isInRange,
        price,
        priceLabel,
      });
    }

    this.calendarDays = days;
  }

  private getPriceForDate(date: Date): number {
    let dayPrice = toFiniteNumber(this.basePrice);
    
    // Check if the date falls within any active seasonal pricing
    const checkDate = new Date(date);
    checkDate.setHours(12, 0, 0, 0);

    for (const season of this.seasonalPricings) {
      if (!season.isActive || season.isDeleted) continue;
      
      const startDate = new Date(season.startDate);
      const endDate = new Date(season.endDate);
      
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) continue;
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      if (checkDate >= startDate && checkDate <= endDate) {
        dayPrice = toFiniteNumber(season.price, dayPrice);
        // Optionally break here or continue to find the latest added. 
        // We'll return the first match for simplicity.
        break;
      }
    }
    
    return roundToTenth(dayPrice);
  }

  private getDateStatus(date: Date): 'available' | 'unavailable' {
    const checkDate = new Date(date);
    checkDate.setHours(12, 0, 0, 0);

    for (const period of this.resolvedAvailabilityPeriods) {
      // Only block days that are booked or blocked
      if (period.status !== 'booked' && period.status !== 'blocked') continue;
      if (!period.startDate) continue;

      const startDate = new Date(period.startDate);
      if (Number.isNaN(startDate.getTime())) continue;
      startDate.setHours(0, 0, 0, 0);

      const endDate = period.endDate ? new Date(period.endDate) : new Date(startDate);
      if (Number.isNaN(endDate.getTime())) continue;
      endDate.setHours(23, 59, 59, 999);

      if (checkDate >= startDate && checkDate <= endDate) {
        return 'unavailable';
      }
    }

    // Everything not explicitly blocked is available
    return 'available';
  }

  private isDateSelected(date: Date): boolean {
    if (!this.selectedStartDate && !this.selectedEndDate) return false;

    const dateStr = this.formatDateForComparison(date);
    const startStr = this.selectedStartDate
      ? this.formatDateForComparison(this.selectedStartDate)
      : null;
    const endStr = this.selectedEndDate ? this.formatDateForComparison(this.selectedEndDate) : null;

    return dateStr === startStr || dateStr === endStr;
  }

  private isDateInRange(date: Date): boolean {
    if (!this.selectedStartDate || !this.selectedEndDate) return false;
    return date > this.selectedStartDate && date < this.selectedEndDate;
  }

  previousMonth(): void {
    if (this.calendarMonth === 0) {
      this.calendarMonth = 11;
      this.calendarYear--;
    } else {
      this.calendarMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    if (this.calendarMonth === 11) {
      this.calendarMonth = 0;
      this.calendarYear++;
    } else {
      this.calendarMonth++;
    }
    this.generateCalendar();
  }

  onDateClick(day: CalendarDay): void {
    if (day.status === 'unavailable' || day.status === 'past') {
      return;
    }

    if (!this.selectedStartDate) {
      this.selectedStartDate = day.fullDate;
      this.selectedEndDate = null;
      this.isSelectingRange = true;
      this.calculateSelectionPrice();
      this.generateCalendar();
      
      this.dateRangeSelected.emit({
        startDate: this.selectedStartDate,
        endDate: this.selectedStartDate,
        totalPrice: this.selectedTotalPrice,
        totalDays: this.selectedTotalDays,
      });
      return;
    }

    if (this.isSelectingRange) {
      if (day.fullDate <= this.selectedStartDate) {
        this.selectedStartDate = day.fullDate;
        this.selectedEndDate = null;
        this.calculateSelectionPrice();
        this.generateCalendar();
        this.dateRangeSelected.emit({
          startDate: this.selectedStartDate,
          endDate: this.selectedStartDate,
          totalPrice: this.selectedTotalPrice,
          totalDays: this.selectedTotalDays,
        });
        return;
      }

      if (this.hasUnavailableDatesInRange(this.selectedStartDate, day.fullDate)) {
        this.selectedStartDate = day.fullDate;
        this.selectedEndDate = null;
        this.calculateSelectionPrice();
        this.generateCalendar();
        this.dateRangeSelected.emit({
          startDate: this.selectedStartDate,
          endDate: this.selectedStartDate,
          totalPrice: this.selectedTotalPrice,
          totalDays: this.selectedTotalDays,
        });
        return;
      }

      this.selectedEndDate = day.fullDate;
      this.isSelectingRange = false;
      this.calculateSelectionPrice();
      this.generateCalendar();

      this.dateRangeSelected.emit({
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
        totalPrice: this.selectedTotalPrice,
        totalDays: this.selectedTotalDays,
      });

      return;
    }

    this.selectedStartDate = day.fullDate;
    this.selectedEndDate = null;
    this.isSelectingRange = true;
    this.calculateSelectionPrice();
    this.generateCalendar();
    
    this.dateRangeSelected.emit({
      startDate: this.selectedStartDate,
      endDate: this.selectedStartDate,
      totalPrice: this.selectedTotalPrice,
      totalDays: this.selectedTotalDays,
    });
  }

  private calculateSelectionPrice(): void {
    if (!this.selectedStartDate) {
      this.selectedTotalPrice = 0;
      this.selectedTotalPriceLabel = '0';
      this.selectedTotalDays = 0;
      return;
    }

    const endDate = this.selectedEndDate || this.selectedStartDate;

    let total = 0;
    let days = 0;
    const current = new Date(this.selectedStartDate);
    
    while (current <= endDate) {
      total += this.getPriceForDate(current);
      days++;
      current.setDate(current.getDate() + 1);
    }
    
    this.selectedTotalPrice = roundToTenth(total);
    this.selectedTotalPriceLabel = this.formatCalendarNumber(this.selectedTotalPrice);
    this.selectedTotalDays = days;
  }

  private hasUnavailableDatesInRange(startDate: Date, endDate: Date): boolean {
    const current = new Date(startDate);
    current.setDate(current.getDate() + 1);

    while (current < endDate) {
      const status = this.getDateStatus(current);
      if (status !== 'available') {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }

    return false;
  }

  clearSelection(): void {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.isSelectingRange = false;
    this.selectedTotalPrice = 0;
    this.selectedTotalPriceLabel = '0';
    this.selectedTotalDays = 0;
    this.generateCalendar();
  }

  private formatCalendarNumber(value: unknown): string {
    return roundToTenth(toFiniteNumber(value)).toLocaleString('en-US', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  }

  getMonthName(): string {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${months[this.calendarMonth]} ${this.calendarYear}`;
  }

  getDayClasses(day: CalendarDay): string {
    let classes =
      'aspect-square rounded-lg border-2 flex flex-col items-center justify-center transition-all relative min-h-[32px] sm:min-h-[40px]';

    if (day.isCurrentMonth) {
      classes += ' text-text-primary';
    } else {
      classes += ' text-text-muted';
    }

    if (day.status === 'available') {
      classes +=
        ' bg-success/10 border-success/20 cursor-pointer hover:scale-105 hover:shadow-md hover:bg-success/20';
    } else if (day.status === 'unavailable') {
      classes += ' bg-surface border-border-subtle cursor-not-allowed opacity-40';
    } else if (day.status === 'past') {
      classes += ' bg-bg-secondary border-border-subtle cursor-not-allowed opacity-60';
    }

    if (day.isSelected) {
      classes += ' !bg-primary !border-primary !text-text-primary ring-2 ring-primary ring-offset-2 ring-offset-bg-base';
    } else if (day.isInRange) {
      classes += ' !bg-primary/20 !border-primary/40';
    }

    if (day.isToday && !day.isSelected) {
      classes += ' ring-2 ring-info ring-offset-1 ring-offset-bg-base';
    }

    return classes;
  }

  private formatDateForComparison(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }
}

